import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Xmark, Copy, Heart } from "iconoir-react";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";

const APP_URL = "https://miloapphouse.com";

export default function ReferFriendDialog({ open, onClose }) {
    const { t } = useT();
    const [copied, setCopied] = useState(false);
    const message = t("refer.message", { url: APP_URL });

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(message);
            setCopied(true);
            toast.success(t("refer.copied"));
            setTimeout(() => setCopied(false), 2000);
        } catch {
            toast.error(t("refer.copy_failed"));
        }
    };

    const share = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: t("refer.share_title"),
                    text: message,
                    url: APP_URL,
                });
            } catch {
                // user cancelled
            }
        } else {
            copy();
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm p-4"
                    onClick={onClose}
                    data-testid="refer-friend-dialog"
                >
                    <motion.div
                        initial={{ y: 40, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: 40, opacity: 0 }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md bg-white rounded-[28px] shadow-[0_2px_4px_rgba(13,42,27,0.04),0_24px_48px_rgba(13,42,27,0.12)] p-6 sm:p-7"
                    >
                        <div className="flex items-start justify-between mb-5">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-[#fbe0dc] flex items-center justify-center">
                                    <Heart className="w-5 h-5 text-[#A53F3F]" />
                                </div>
                                <div>
                                    <div className="font-display text-2xl font-bold tracking-tight leading-none">
                                        {t("refer.headline")}
                                    </div>
                                    <div className="text-xs text-[#5b6d63] font-semibold mt-1">{t("refer.subline")}</div>
                                </div>
                            </div>
                            <button
                                data-testid="refer-close-btn"
                                onClick={onClose}
                                className="w-8 h-8 rounded-full hover:bg-[#faf5ea] flex items-center justify-center"
                                aria-label="close"
                            >
                                <Xmark className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="bg-[#faf5ea] rounded-2xl p-4 text-sm leading-relaxed text-[#0d2a1b] mb-4">
                            {message}
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <button
                                data-testid="refer-copy-btn"
                                onClick={copy}
                                className="flex items-center justify-center gap-2 bg-white border border-[#0d2a1b]/10 rounded-xl py-3 font-semibold text-sm hover:bg-[#faf5ea] transition-colors"
                            >
                                <Copy className="w-4 h-4" /> {copied ? t("refer.copied_btn") : t("refer.copy_btn")}
                            </button>
                            <button
                                data-testid="refer-share-btn"
                                onClick={share}
                                className="flex items-center justify-center gap-2 bg-[#2F6B43] text-white rounded-xl py-3 font-semibold text-sm hover:bg-[#255636] transition-colors shadow-[0_4px_12px_rgba(47,107,67,0.25)]"
                            >
                                <Heart className="w-4 h-4" /> {t("refer.share_btn")}
                            </button>
                        </div>

                        <div className="text-[11px] text-[#5b6d63] text-center mt-4">
                            {t("refer.footer")}
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
